import { haversineMiles } from '../geo-distance';

export interface LatLng {
  lat: number;
  lng: number;
}

export type MapPinKind = 'fired' | 'current';

export interface MapPin {
  kind: MapPinKind;
  position: LatLng;
}

export interface MapPlan {
  pins: MapPin[];
  drift: { from: LatLng; to: LatLng; label: string } | null;
}

export const PIN_COLORS: Record<MapPinKind, string> = {
  fired: '#b00020',
  current: '#0b5fa5',
};

export const PIN_LABEL_KEYS: Record<MapPinKind, string> = {
  fired: '@whereAlertFired',
  current: '@assetNow',
};

// (0,0) is a missing fix, not a position off the coast of Ghana.
export function usablePosition(lat?: number | null, lng?: number | null): LatLng | null {
  if (lat == null || lng == null || !isFinite(lat) || !isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

export function midpoint(a: LatLng, b: LatLng): LatLng {
  return { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
}

export function buildMapPlan(
  fired: LatLng | null,
  current: LatLng | null,
  driftLabel: string | null,
): MapPlan | null {
  if (!fired) return null;
  const pins: MapPin[] = [{ kind: 'fired', position: fired }];
  if (current) pins.push({ kind: 'current', position: current });
  const drift = current && driftLabel ? { from: fired, to: current, label: driftLabel } : null;
  return { pins, drift };
}

export const MAP_MAX_ZOOM = 16;
export const MAP_TIGHT_ZOOM = 19;
// Under ~100m apart the default cap leaves the pins stacked, so the fit is allowed deeper.
const TIGHT_MILES = 0.06;
// Under ~10m the pin graphics overlap at any zoom, so the later one is nudged aside.
const STACKED_MILES = 0.006;

export function pinSpreadMiles(pins: MapPin[]): number {
  let widest = 0;
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) {
      const a = pins[i].position;
      const b = pins[j].position;
      widest = Math.max(widest, haversineMiles(a.lat, a.lng, b.lat, b.lng));
    }
  }
  return widest;
}

export function fitMaxZoom(pins: MapPin[]): number {
  return pins.length > 1 && pinSpreadMiles(pins) <= TIGHT_MILES ? MAP_TIGHT_ZOOM : MAP_MAX_ZOOM;
}

export function stackedOn(pin: MapPin, others: MapPin[]): boolean {
  return others.some(o => haversineMiles(o.position.lat, o.position.lng, pin.position.lat, pin.position.lng) <= STACKED_MILES);
}
